import React, { useId } from "react";
import { Trans } from "react-i18next";
import type { WaveformDisplayMode, RowHeightMode } from "./eegViewerDisplayHelpers";

const DISPLAY_MODE_OPTIONS: Array<{
  value: WaveformDisplayMode;
  label: string;
}> = [
  { value: "stacked", label: "Stacked" },
  { value: "butterfly", label: "Butterfly" },
];


const ROW_HEIGHT_OPTIONS: Array<{ value: RowHeightMode; label: string }> = [
  { value: "compact", label: "Compact" },
  { value: "standard", label: "Cozy" },
  { value: "tall", label: "Comfortable" },
];


function useSafeDomId(prefix: string): string {
  const reactId = useId();
  return `${prefix}-${reactId.replace(/[^A-Za-z0-9_-]/g, "")}`;
}

type EEGWaveformVisibilityControlsProps = {
  currentSensitivity: number;
  pendingSensitivity: number | null;
  sensitivitySteps: readonly number[];
  canIncreaseSensitivity: boolean;
  canDecreaseSensitivity: boolean;
  autoFitDisabled?: boolean;
  showAutoFit?: boolean;
  onIncreaseSensitivity: () => void;
  onDecreaseSensitivity: () => void;
  onSensitivityChange: (nextValue: number) => void;
  onAutoFit: () => void;
};

type EEGWaveformLayoutControlsProps = {
  displayMode: WaveformDisplayMode;
  rowHeightMode: RowHeightMode;
  allowTraceOverflow: boolean;
  refitOnChannelChange: boolean;
  rowHeightDisabled?: boolean;
  onDisplayModeChange: (nextValue: WaveformDisplayMode) => void;
  onRowHeightModeChange: (nextValue: RowHeightMode) => void;
  onAllowTraceOverflowChange: (nextValue: boolean) => void;
  onRefitOnChannelChange: (nextValue: boolean) => void;
  onResetDisplay: () => void;
};

export function EEGWaveformLayoutControls({
  displayMode,
  rowHeightMode,
  allowTraceOverflow,
  refitOnChannelChange,
  rowHeightDisabled = false,
  onDisplayModeChange,
  onRowHeightModeChange,
  onAllowTraceOverflowChange,
  onRefitOnChannelChange,
  onResetDisplay,
}: EEGWaveformLayoutControlsProps) {
  const displayModeSelectId = useSafeDomId("eeg-display-mode");
  const rowHeightSelectId = useSafeDomId("eeg-row-height");

  return (
    <div className="eeg-toolbar-control-bundle">
      <div className="control-group control-group--display-mode">
        <label className="visually-hidden" htmlFor={displayModeSelectId}>
          Display
        </label>
        <select
          id={displayModeSelectId}
          aria-label="Display:"
          className="eeg-signal-select eeg-signal-select--displaymode"
          value={displayMode}
          onChange={(event) =>
            onDisplayModeChange(event.target.value as WaveformDisplayMode)
          }
        >
          {DISPLAY_MODE_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      <div className="control-group control-group--row-height">
        <label className="visually-hidden" htmlFor={rowHeightSelectId}>
          Workspace Density
        </label>
        <select
          id={rowHeightSelectId}
          aria-label="Workspace Density"
          className="eeg-signal-select eeg-signal-select--rowheight"
          value={rowHeightMode}
          onChange={(event) =>
            onRowHeightModeChange(event.target.value as RowHeightMode)
          }
          disabled={rowHeightDisabled}
          title={
            rowHeightDisabled
              ? "Workspace Density is available in stacked display."
              : undefined
          }
        >
          {ROW_HEIGHT_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}


export function EEGWaveformVisibilityControls({
  currentSensitivity,
  pendingSensitivity,
  sensitivitySteps,
  canIncreaseSensitivity,
  canDecreaseSensitivity,
  autoFitDisabled = false,
  showAutoFit = true,
  onIncreaseSensitivity,
  onDecreaseSensitivity,
  onSensitivityChange,
  onAutoFit,
}: EEGWaveformVisibilityControlsProps) {
  const sensitivitySelectId = useSafeDomId("eeg-sensitivity");
  const autoFitButtonId = useSafeDomId("eeg-auto-fit");

  return (
    <div className="eeg-toolbar-control-bundle">
      <div className="control-group control-group--sensitivity">
        <label className="eeg-control-label" htmlFor={sensitivitySelectId}>
          Sensitivity
        </label>
        <select
          id={sensitivitySelectId}
          aria-label="Sensitivity (uV/mm)"
          title="Sensitivity: microvolts of input per millimetre of trace deflection"
          className="eeg-signal-select eeg-signal-select--sensitivity"
          value={pendingSensitivity ?? currentSensitivity}
          onChange={(event) =>
            onSensitivityChange(Number.parseFloat(event.target.value))
          }
        >
          {sensitivitySteps.map((sensitivity) => (
            <option key={sensitivity} value={sensitivity}>
              {sensitivity} µV/mm
            </option>
          ))}
        </select>
      </div>

      {showAutoFit ? (
        <div className="control-group control-group--autofit">
          <button
            type="button"
            id={autoFitButtonId}
            className="eeg-toolbar-filter-toggle eeg-auto-fit-btn"
            onClick={onAutoFit}
            disabled={autoFitDisabled}
            title="Fit visible traces to the current stacked view."
            aria-label="Auto Fit"
          >
            <Trans ns="features" i18nKey="ui.copy.d9136540a499" />
          </button>
        </div>
      ) : null}
    </div>
  );
}

export function EEGWaveformAutoFitControl({
  disabled = false,
  onAutoFit,
}: {
  disabled?: boolean;
  onAutoFit: () => void;
}) {
  const autoFitButtonId = useSafeDomId("eeg-auto-fit");
  return (
    <div className="control-group control-group--autofit" key="auto-fit">
      <button
        type="button"
        id={autoFitButtonId}
        className="eeg-toolbar-filter-toggle eeg-auto-fit-btn"
        onClick={onAutoFit}
        disabled={disabled}
        title="Fit visible traces to the current stacked view."
        aria-label="Auto Fit"
      >
        <Trans ns="features" i18nKey="ui.copy.d9136540a499" />
      </button>
    </div>
  );
}
